import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// Re-authored from ../design-system/components/buttons/IconButton.jsx.
const iconButtonVariants = cva(
  'inline-flex items-center justify-center border border-transparent text-primary transition ' +
    'disabled:opacity-45 disabled:cursor-not-allowed ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
    'focus-visible:ring-offset-[var(--bg-page)]',
  {
    variants: {
      variant: {
        ghost: 'bg-transparent enabled:hover:bg-[var(--ollio-navy-light)] enabled:active:bg-[var(--ollio-cream-200)]',
        solid: 'bg-primary text-primary-foreground enabled:hover:bg-[var(--primary-hover)]',
        outline: 'bg-card border-border enabled:hover:border-primary enabled:hover:bg-[var(--ollio-navy-light)]',
      },
      size: { sm: 'h-8 w-8', md: 'h-10 w-10', lg: 'h-12 w-12' },
      round: { true: 'rounded-full', false: 'rounded-[var(--radius-control)]' },
    },
    defaultVariants: { variant: 'ghost', size: 'md', round: false },
  },
);

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'>,
    VariantProps<typeof iconButtonVariants> {
  label: string;
  children: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { variant, size, round, label, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cn(iconButtonVariants({ variant, size, round }), className)}
      {...rest}
    >
      {children}
    </button>
  );
});
